import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { trackPageView } from '../utils/analytics';

const articles = [
  {
    title: 'How to Improve LCP (Largest Contentful Paint)',
    excerpt: 'Learn the most effective techniques to reduce LCP and pass the Core Web Vitals threshold of 2.5 seconds.',
    href: '/blog/how-to-improve-lcp',
    published: '2025-03-12',
    readMinutes: 8,
  },
  {
    title: 'The Complete Core Web Vitals Guide',
    excerpt: 'Understand LCP, INP, and CLS — what they measure, what thresholds mean, and how to improve each metric.',
    href: '/blog/core-web-vitals-guide',
    published: '2025-02-20',
    readMinutes: 9,
  },
  {
    title: '10 Website Speed Optimisation Tips',
    excerpt: 'A practical checklist covering images, caching, JavaScript, and fonts to reduce page load time.',
    href: '/blog/website-speed-optimisation-tips',
    published: '2025-01-08',
    readMinutes: 6,
  },
];

export const Blog = () => {
  useEffect(() => {
    trackPageView('/blog');
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Blog & Resources | Web Speed Advisor"
        description="Practical guides on website speed optimisation, Core Web Vitals, and page performance. Learn how to improve LCP, CLS, and INP."
        canonical="https://webspeedadvisor.com/blog"
      />

      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Link to="/" className="inline-block text-sm text-slate-400 hover:text-white mb-6">
            ← Back to Home
          </Link>
          <h1 className="text-5xl md:text-6xl font-black font-chivo mb-6">
            Blog & Resources
          </h1>
          <p className="text-xl text-slate-300">
            Practical guides on website speed optimisation, Core Web Vitals, and page performance.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="grid gap-8">
          {articles.map((article) => (
            <article key={article.href} className="bg-white rounded-xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-4 mb-3 text-xs text-slate-400">
                <time dateTime={article.published}>
                  {new Date(article.published).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </time>
                <span>{article.readMinutes} min read</span>
              </div>
              <h2 className="text-2xl font-bold font-chivo text-slate-900 mb-3">
                <Link to={article.href} className="hover:text-primary transition-colors">
                  {article.title}
                </Link>
              </h2>
              <p className="text-slate-600 mb-4">{article.excerpt}</p>
              <Link
                to={article.href}
                className="inline-flex items-center text-sm font-semibold text-primary hover:underline"
              >
                Read article →
              </Link>
            </article>
          ))}
        </div>

        <section className="bg-primary text-white rounded-xl p-8 text-center mt-16">
          <h2 className="text-3xl font-bold font-chivo mb-4">
            Ready to analyse your website?
          </h2>
          <p className="text-lg mb-6 text-blue-100">
            Get instant page speed results, Core Web Vitals insights, and AI-powered recommendations.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-primary font-bold px-8 py-4 rounded-full transition-all"
          >
            <Zap size={24} />
            Analyse your website for free
          </Link>
        </section>
      </div>
    </div>
  );
};

export default Blog;
